/**
 * Bookings API client for the business app.
 *
 * Single source of truth for the staff "day of bookings" view. Wraps
 * GET /facilities/{id}/bookings/daily?date= behind the BFF proxy. Consumed by:
 *   - arena/arena-workspace.tsx       (live court occupancy derived from "now")
 *   - check-in/check-in-workspace.tsx (today's expected parties)
 *
 * Both render the SAME real backend data — never fabricated samples.
 * The Booking shape is a minimal view of the generated StaffBookingResponse.
 * booking_id / court_id are opaque ids used only for
 * React keys and court-name lookup — never rendered in the UI (no-UUID rule).
 */
import { httpClient, isApiError } from "@bw/shared/api"
export {
  searchBusinessReservationOperations as searchReservationOperations,
} from "@bw/shared/api"
export type {
  ReservationOperation,
  ReservationOperationSort,
  ReservationOperationsPage,
  ReservationOperationsParams,
} from "@bw/shared/api"
import type { Schema } from "@bw/shared/types/generated"

// The money fields are branded from the generated StaffBookingResponse (the
// wire shape the operator list routes return) so they can't be redeclared as
// plain number/string (ADR-005, the no-money-arithmetic guardrail). `Booking`
// is a deliberately MINIMAL view (the staff day view does not need
// calendar_event_id/facility_id/created_at), so the fields are referenced
// individually rather than aliasing the whole generated model.
type StaffBookingWire = Schema["StaffBookingResponse"]

/**
 * Body and result for a staff-created booking. Payment is online only: the
 * required `payment` is "free" or "pay_link", and the result reports the
 * payment status. A pay link reaches the player by notification; staff read
 * it from the booking's pay links.
 */
export type StaffBookingBody = Schema["StaffBookingCreate"]
export type StaffBookingResult = Schema["StaffBookingResult"]
export type StaffBookingQuoteBody = Schema["StaffBookingQuoteRequest"]
export type StaffBookingQuoteResult = Schema["StaffBookingQuoteResponse"]

/**
 * Preview the exact allocation and price the staff-create operation will use.
 * A facility with no matching rule receives the server's explicit $0 fallback.
 */
export function getStaffBookingQuote(
  facilityId: string,
  body: StaffBookingQuoteBody,
): Promise<StaffBookingQuoteResult> {
  return httpClient.post<StaffBookingQuoteResult>(
    `/facilities/${facilityId}/staff-bookings/quote`,
    body,
  )
}

/**
 * Create a confirmed reservation for a facility-known player. The server owns
 * authorization, allocation, availability revalidation, and the final price;
 * the Business app never sends an amount or payment credential.
 */
export function createStaffBooking(
  facilityId: string,
  body: StaffBookingBody,
): Promise<StaffBookingResult> {
  return httpClient.post<StaffBookingResult>(
    `/facilities/${facilityId}/staff-bookings`,
    body,
  )
}

/** Minimal generated operator-booking read model used by the Business app. */
export type Booking = Pick<
  StaffBookingWire,
  | "booking_id"
  | "court_id"
  | "user_id"
  | "display_name"
  | "event_name"
  | "start_time"
  | "end_time"
  | "amount_charged"
  | "currency"
  | "status"
  | "refund_amount"
  | "cancelled_at"
  | "cancellation_recovery_pending"
  | "cancellation_allowed"
  | "cancellation_unavailable_reason"
>

/**
 * Body for the staff court-block POST. Aliased to the generated
 * StaffCourtBlockCreate (ADR-005). The staff actor comes from the auth token,
 * not the body. The backend validates court_ids (≥1), event_name (non-blank),
 * the schedule, and the invited-player existence — the FE only collects inputs.
 */
export type StaffCourtBlockBody = Schema["StaffCourtBlockCreate"]

/**
 * The created block(s) for one court-block request. Aliased to the generated
 * StaffCourtBlockResponse — ``blocks`` is one $0 BookingResponse per court.
 */
export type StaffCourtBlockResult = Schema["StaffCourtBlockResponse"]

/** Exact-interval request and non-binding server verdict for staff court blocks. */
export type StaffCourtBlockPreviewBody = Schema["StaffCourtBlockPreviewRequest"]
export type StaffCourtBlockPreview = Schema["StaffCourtBlockPreviewResponse"]
export type StaffCourtBlockCourtVerdict = Schema["StaffCourtBlockCourtVerdict"]
export type StaffCourtBlockConflict = Schema["StaffCourtBlockConflictDetail"]

/** Ask the scheduling authority about every candidate court in one request. */
export async function previewStaffCourtBlock(
  facilityId: string,
  body: StaffCourtBlockPreviewBody,
): Promise<StaffCourtBlockPreview> {
  const value = await httpClient.post<unknown>(
    `/facilities/${facilityId}/staff-court-blocks/preview`,
    body,
  )
  const decoded = decodeStaffCourtBlockPreview(value, facilityId, body)
  if (!decoded) throw new Error("Court availability response was invalid.")
  return decoded
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null
}

const RFC3339_INSTANT =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d{1,6})?(Z|[+-]\d{2}:\d{2})$/
const IANA_TIME_ZONE_NAME = /^[A-Za-z][A-Za-z0-9_+-]*(?:\/[A-Za-z0-9_+-]+)*$/

function isValidInstant(value: unknown): value is string {
  if (typeof value !== "string") return false
  const match = RFC3339_INSTANT.exec(value)
  if (!match) return false

  const [, yearText, monthText, dayText, hourText, minuteText, secondText, offset] = match
  const year = Number(yearText)
  const month = Number(monthText)
  const day = Number(dayText)
  const hour = Number(hourText)
  const minute = Number(minuteText)
  const second = Number(secondText)

  const calendarDate = new Date(0)
  calendarDate.setUTCFullYear(year, month - 1, day)
  calendarDate.setUTCHours(hour, minute, second, 0)
  if (
    calendarDate.getUTCFullYear() !== year ||
    calendarDate.getUTCMonth() !== month - 1 ||
    calendarDate.getUTCDate() !== day ||
    calendarDate.getUTCHours() !== hour ||
    calendarDate.getUTCMinutes() !== minute ||
    calendarDate.getUTCSeconds() !== second
  ) {
    return false
  }

  if (offset !== "Z") {
    const offsetHour = Number(offset.slice(1, 3))
    const offsetMinute = Number(offset.slice(4, 6))
    if (offsetHour > 23 || offsetMinute > 59) return false
  }

  return Number.isFinite(Date.parse(value))
}

function isValidTimeZone(value: unknown): value is string {
  if (typeof value !== "string" || !IANA_TIME_ZONE_NAME.test(value)) return false
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value }).format()
    return true
  } catch {
    return false
  }
}

function representsSameInstant(left: string, right: string): boolean {
  return Date.parse(left) === Date.parse(right)
}

function isConflictSummary(value: unknown): value is Schema["StaffCourtBlockConflictSummary"] {
  if (!isRecord(value)) return false
  return (
    typeof value.event_type === "string" &&
    (value.source_type === undefined ||
      value.source_type === null ||
      typeof value.source_type === "string") &&
    isValidInstant(value.start_time) &&
    isValidInstant(value.end_time) &&
    Date.parse(value.end_time) > Date.parse(value.start_time)
  )
}

function isConflictVerdict(value: unknown): value is StaffCourtBlockCourtVerdict {
  if (!isRecord(value) || !Array.isArray(value.conflicts)) return false
  return (
    typeof value.court_id === "string" &&
    typeof value.court_name === "string" &&
    value.available === false &&
    value.unavailable_reason === "court_conflict" &&
    value.conflicts.every(isConflictSummary)
  )
}

const STAFF_COURT_UNAVAILABLE_REASONS = new Set([
  "court_disabled",
  "outside_operating_hours",
  "court_conflict",
])

function isPreviewCourtVerdict(
  value: unknown,
  expectedCourtIds: ReadonlySet<string>,
  startTime: string,
  endTime: string,
): value is StaffCourtBlockCourtVerdict {
  if (
    !isRecord(value) ||
    typeof value.court_id !== "string" ||
    !expectedCourtIds.has(value.court_id) ||
    typeof value.court_name !== "string" ||
    typeof value.available !== "boolean" ||
    !Array.isArray(value.conflicts) ||
    !value.conflicts.every(isConflictSummary)
  ) {
    return false
  }

  if (value.available) {
    return (
      (value.unavailable_reason === undefined || value.unavailable_reason === null) &&
      value.conflicts.length === 0
    )
  }
  if (
    typeof value.unavailable_reason !== "string" ||
    !STAFF_COURT_UNAVAILABLE_REASONS.has(value.unavailable_reason)
  ) {
    return false
  }
  if (value.unavailable_reason !== "court_conflict") {
    return value.conflicts.length === 0
  }
  return (
    value.conflicts.length > 0 &&
    value.conflicts.every(
      (conflict) =>
        Date.parse(conflict.start_time) < Date.parse(endTime) &&
        Date.parse(conflict.end_time) > Date.parse(startTime),
    )
  )
}

function decodeStaffCourtBlockPreview(
  value: unknown,
  facilityId: string,
  body: StaffCourtBlockPreviewBody,
): StaffCourtBlockPreview | null {
  if (
    !isRecord(value) ||
    value.facility_id !== facilityId ||
    value.is_binding !== false ||
    !isValidInstant(value.start_time) ||
    !isValidInstant(value.end_time) ||
    !representsSameInstant(value.start_time, body.start_time) ||
    !representsSameInstant(value.end_time, body.end_time) ||
    !isValidInstant(value.checked_at) ||
    !isValidTimeZone(value.timezone) ||
    !Array.isArray(value.courts)
  ) {
    return null
  }

  const expectedCourtIds = new Set(body.court_ids)
  const startTime = value.start_time
  const endTime = value.end_time
  if (
    expectedCourtIds.size !== body.court_ids.length ||
    value.courts.length !== expectedCourtIds.size ||
    !value.courts.every((court) =>
      isPreviewCourtVerdict(court, expectedCourtIds, startTime, endTime),
    )
  ) {
    return null
  }
  const returnedCourtIds = new Set(value.courts.map((court) => court.court_id))
  if (
    returnedCourtIds.size !== expectedCourtIds.size ||
    [...expectedCourtIds].some((courtId) => !returnedCourtIds.has(courtId))
  ) {
    return null
  }

  return value as StaffCourtBlockPreview
}

/** Decode only the generated, structured 409 contract; never parse error text. */
export function getStaffCourtBlockConflict(error: unknown): StaffCourtBlockConflict | null {
  if (!isApiError(error) || error.status !== 409 || error.code !== "STAFF_COURT_BLOCK_CONFLICT") {
    return null
  }
  const detail = error.details
  if (
    detail?.code !== "STAFF_COURT_BLOCK_CONFLICT" ||
    typeof detail.message !== "string" ||
    !isValidInstant(detail.start_time) ||
    !isValidInstant(detail.end_time) ||
    Date.parse(detail.end_time) <= Date.parse(detail.start_time) ||
    !isValidTimeZone(detail.timezone) ||
    !Array.isArray(detail.conflicts) ||
    !detail.conflicts.every(isConflictVerdict)
  ) {
    return null
  }
  return {
    code: "STAFF_COURT_BLOCK_CONFLICT",
    message: detail.message,
    start_time: detail.start_time,
    end_time: detail.end_time,
    timezone: detail.timezone,
    conflicts: detail.conflicts,
  }
}

/** Correlate submit-time conflict evidence to the immutable attempted write. */
export function staffCourtBlockConflictMatchesAttempt(
  conflict: StaffCourtBlockConflict,
  attempt: { courtIds: string[]; startTime: string; endTime: string },
): boolean {
  if (
    !representsSameInstant(conflict.start_time, attempt.startTime) ||
    !representsSameInstant(conflict.end_time, attempt.endTime) ||
    conflict.conflicts.length === 0
  ) {
    return false
  }

  const selectedCourtIds = new Set(attempt.courtIds)
  const conflictCourtIds = new Set(conflict.conflicts.map((row) => row.court_id))
  const attemptStart = Date.parse(attempt.startTime)
  const attemptEnd = Date.parse(attempt.endTime)
  return (
    conflictCourtIds.size === conflict.conflicts.length &&
    conflict.conflicts.every((row) => {
      const summaries = row.conflicts ?? []
      return (
        selectedCourtIds.has(row.court_id) &&
        summaries.length > 0 &&
        summaries.every(
          (summary) =>
            Date.parse(summary.start_time) < attemptEnd &&
            Date.parse(summary.end_time) > attemptStart,
        )
      )
    })
  )
}

/**
 * Block one or more of the facility's OWN courts for an event ($0, no charge,
 * #1674) — POST /facilities/{id}/staff-court-blocks. RBAC (facility:schedule)
 * and all validation/atomicity are enforced by the backend; a conflict on ANY
 * court rejects the whole request (409). The FE never fabricates the result —
 * the caller re-lists the day on success.
 */
export function staffBlockCourt(
  facilityId: string,
  body: StaffCourtBlockBody,
): Promise<StaffCourtBlockResult> {
  return httpClient.post<StaffCourtBlockResult>(
    `/facilities/${facilityId}/staff-court-blocks`,
    body,
  )
}

/**
 * One known-player hit for the court-block invite picker. Aliased to the
 * generated FacilityPlayerSearchResult: ``display_name`` is the RESOLVED name
 * (the backend never returns the raw uid as a name — no-UID-in-UI); ``user_id``
 * is the opaque id the block POST sends, never rendered.
 */
export type FacilityPlayerSearchResult = Schema["FacilityPlayerSearchResult"]

/**
 * Search players KNOWN to the facility by name for the invite picker
 * (GET /facilities/{id}/players/search). RBAC (facility:schedule) is enforced by
 * the backend; results carry a resolved display_name only. Reused by the staff
 * court-block invite step (#1674).
 */
export function searchFacilityPlayers(
  facilityId: string,
  q: string,
  limit = 10,
): Promise<FacilityPlayerSearchResult[]> {
  const sp = new URLSearchParams({ q, limit: String(limit) })
  return httpClient.get<FacilityPlayerSearchResult[]>(
    `/facilities/${facilityId}/players/search?${sp.toString()}`,
  )
}

/** YYYY-MM-DD in local time — the backend daily endpoint parses an ISO date. */
export function toDateParam(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, "0")
  const d = String(date.getDate()).padStart(2, "0")
  return `${y}-${m}-${d}`
}

/** All of a facility's bookings for a single day (staff view). */
export function listDailyBookings(facilityId: string, date: Date): Promise<Booking[]> {
  return httpClient.get<Booking[]>(
    `/facilities/${facilityId}/bookings/daily?date=${toDateParam(date)}`,
  )
}

/** A short-lived, server-authored cancellation decision for one booking. */
export type BookingCancellationQuote = Schema["BookingCancellationQuoteResponse"]

/** The committed cancellation and the quote/policy evidence it consumed. */
export type BookingCancellationResult = Schema["BookingCancellationResponse"]

/**
 * Request an authoritative cancellation quote. The booking id is the only
 * input: amount, currency, policy, cutoff, fee, and refund all come from the
 * server-owned booking and policy state.
 */
export function createBookingCancellationQuote(
  bookingId: string,
): Promise<BookingCancellationQuote> {
  return httpClient.post<BookingCancellationQuote>(
    `/facilities/bookings/${bookingId}/cancellation-quotes`,
  )
}

/**
 * Body for the cancel POST. Aliased to the generated BookingCancelRequest
 * (ADR-005) so refund_amount_override stays a branded Money (Decimal string) —
 * not a plain number/string. Honoured ONLY for facility:refund_override holders;
 * `reason` is required by the backend when the override deviates from policy.
 */
export type BookingCancelBody = Schema["BookingCancelRequest"]

/**
 * Commit a server-issued cancellation quote. The complete quote/idempotency
 * body is required so retries cannot create a second cancellation or commit a
 * decision the operator did not review.
 */
export function cancelBooking(
  bookingId: string,
  body: BookingCancelBody,
): Promise<BookingCancellationResult> {
  return httpClient.post<BookingCancellationResult>(
    `/facilities/bookings/${bookingId}/cancel`,
    body,
  )
}

/** Resume an already-committed cancellation after its request body was lost. */
export function recoverBookingCancellation(bookingId: string): Promise<BookingCancellationResult> {
  return httpClient.post<BookingCancellationResult>(
    `/facilities/bookings/${bookingId}/cancel/recover`,
  )
}

/**
 * Mark a confirmed booking as a no-show (staff action, no_show added in #1329).
 * Confirmed-only and RBAC (facility:schedule) are enforced by the backend
 * (POST /facilities/bookings/{id}/no-show). The endpoint takes no inputs — an
 * EMPTY body — because a no-show forfeits the charge: no money is moved and the
 * calendar event stays. The FE never optimistically flips the row; the caller
 * re-lists on success and surfaces the error on failure. Returns the updated
 * BookingResponse (status: no_show; amount_charged unchanged, no refund).
 */
export function markNoShow(bookingId: string): Promise<Schema["BookingResponse"]> {
  return httpClient.post<Schema["BookingResponse"]>(`/facilities/bookings/${bookingId}/no-show`, {})
}
